/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';

import { ReactComponent as CreditCardIcon } from '../../../assets/icons/credit-card.svg';
import { ReactComponent as ApplePayLogo } from '../../../assets/icons/payment-apple.svg';
import { ReactComponent as VisaLogo } from '../../../assets/icons/payment-visa.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
// import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SelectField from '../../../shared-components/select-field/SelectField';
import paymentGatewayApi from '../../../utils/api/v2/payment-gateway';

export interface ReviewData {
  gatewayName: string;
  gatewayLogo: string;
  status: string;
  details: { label: string; value: string }[];
}

interface AddGatewayProperties {
  onCancel?: () => void;
  onSuccess?: () => void;
  editData?: any;
}

/**
 * Radio Button Value
 * @param {any} value - The value
 * @returns {any} The result
 */
const getRadioButtonValue = (value: any): string => {
  if (!value) return 'b2c';
  if (Array.isArray(value)) {
    if (value.includes('b2c') && value.includes('b2b')) {
      return 'both';
    }
    return value[0] || 'b2c';
  }
  return value;
};

/**
 * Country Name helper
 * @param {any} countryCode The country code (e.g. SA, AE, US, IN)
 * @returns {any} The formatted country name string
 */
const getCountryName = (countryCode: string) => {
  const countries: Record<string, string> = {
    IN: 'India',
    SA: 'Saudi Arabia',
    US: 'United States',
    AE: 'UAE',
  };
  return countries[countryCode] || countryCode;
};

/**
 * Business Type Label helper
 * @param {any} type - The type
 */
const getBusinessTypeLabel = (type: any): string => {
  if (Array.isArray(type)) {
    return type
      .map((t) => {
        if (t.toLowerCase() === 'b2c') return 'B2C Payment';
        if (t.toLowerCase() === 'b2b') return 'B2B Payout';
        return t;
      })
      .join(', ');
  }
  const typeString = String(type).toLowerCase();
  if (typeString === 'b2c') return 'B2C Payment';
  if (typeString === 'b2b') return 'B2B Payout';
  return String(type);
};

/**
 * Mada logo component
 * @returns {any} React node
 */
function MadaLogoComponent() {
  return (
    <div className="mada-logo-inline method-logo-svg" title="Mada">
      mada
    </div>
  );
}

/**
 * Add Gateway Flow Container
 * @param {any} props The component props
 * @param {any} props.onCancel The function to cancel the flow
 * @param {any} props.onSuccess - The onSuccess prop
 * @param {any} props.editData Optional data for edit flow
 * @returns {any} React node
 */
function AddGateway({
  onCancel = () => { },
  onSuccess = () => { },
  editData = null,
}: AddGatewayProperties) {
  const { activePortal } = usePortal();
  const isCPO = activePortal === PortalType.NETWORKS;

  const dispatch = useAppDispatch();
  const [step, setStep] = useState<number>(1);
  const [gatewayOptions, setGatewayOptions] = useState<any[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<string>(editData ? editData.key : '');
  const [selectedGatewayData, setSelectedGatewayData] = useState<any>(null);
  const [formData, setFormData] = useState<Record<string, string | number | string[]>>(() => {
    if (editData) {
      const editBusinessType = (editData.businessType || 'b2b').toLowerCase();
      let businessType: string | string[] = editBusinessType;
      if (isCPO) {
        businessType = editBusinessType === 'both' ? ['b2c', 'b2b'] : [editBusinessType];
      }
      return {
        status: editData.status || 'active',
        businessType,
        country: editData.country || 'SA',
        paymentMethods: editData.paymentMethods || [],
      };
    }
    return {
      status: 'active',
      businessType: isCPO ? ['b2c'] : 'b2c',
      country: 'SA',
      paymentMethods: [],
    };
  });
  const [isInitialEdit, setIsInitialEdit] = useState<boolean>(!!editData);

  /**
   * fetches the list of available payment gateways from the API and sets the options for the gateway selection dropdown
   */
  const getGatewayList = async () => {
    if (!formData.country || !formData.businessType) {
      return;
    }

    try {
      if (isInitialEdit) {
        setIsInitialEdit(false);
      } else {
        setSelectedGateway('');
        setSelectedGatewayData(null);
        setFormData((previous) => ({
          status: previous.status,
          businessType: previous.businessType,
          country: previous.country,
          paymentMethods: [],
        }));
      }

      const response = await paymentGatewayApi.fetchPaymentGatewaysList({
        country: formData.country,
        useCase: formData.businessType,
      });

      console.log('gateway list', response.data);

      setGatewayOptions(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * fetches the details of a selected payment gateway based on the gateway key
   * It makes an API call to retrieve the gateway details, including supported payment methods, transaction types, and configuration fields
   * The retrieved data is stored in the selectedGatewayData state for use in the configuration step
   * @param {any} gatewayKey - The gatewayKey
   */
  const getGatewayDetails = async (gatewayKey: string) => {
    try {
      const gatewayData = gatewayOptions.find((gateway: any) => gateway.key === gatewayKey);

      if (!gatewayData) {
        return;
      }

      setSelectedGatewayData(gatewayData);

      if (gatewayData?.configFields) {
        const initialValues: Record<string, string> = {};

        gatewayData.configFields.forEach((field: any) => {
          let savedValue = '';
          if (editData && editData.credentials) {
            const normalizedKey = field.key.toLowerCase().replace(/_/g, '');
            const matchingEntry = Object.entries(editData.credentials).find(
              ([k]) => k.toLowerCase().replace(/_/g, '') === normalizedKey,
            );
            if (matchingEntry) {
              savedValue = String(matchingEntry[1]);
            }
          }
          initialValues[field.key] = savedValue;
        });

        setFormData((previous) => ({
          ...previous,
          ...initialValues,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Select Change
   * @param {string} name - The name of the field
   * @param {string} value - The selected value
   */
  const handleSelectChange = async (name: string, value: string) => {
    if (name === 'gateway') {
      setSelectedGateway(value);
      await getGatewayDetails(value);
    }
  };

  /**
   * handleInputChange
   * @param {string} name - The name of the field
   * @param {any} value - The input value
   */
  const handleInputChange = (name: string, value: string | number | string[]) => {
    if (name === 'businessType') {
      const arrayValue = value === 'both' ? ['b2c', 'b2b'] : [value as string];
      setFormData((previous) => ({
        ...previous,
        [name]: isCPO ? arrayValue : (value as string),
      }));
    } else {
      setFormData((previous) => ({ ...previous, [name]: value }));
    }
  };

  useEffect(() => {
    if (formData.country && formData.businessType) {
      getGatewayList();
    }
  }, [formData.country, formData.businessType]);

  useEffect(() => {
    if (editData) {
      setIsInitialEdit(true);
      const editBusinessType = (editData.businessType || 'b2b').toLowerCase();
      let businessTypeValue: string | string[] = editBusinessType;
      if (isCPO) {
        businessTypeValue = editBusinessType === 'both' ? ['b2c', 'b2b'] : [editBusinessType];
      }

      setFormData((previous) => ({
        ...previous,
        country: editData.country || 'SA',
        businessType: businessTypeValue,
        paymentMethods: editData.paymentMethods || [],
      }));
      setSelectedGateway(editData.key);
      setStep(1);
    }
  }, [editData, isCPO]);

  useEffect(() => {
    if (editData && gatewayOptions.length > 0 && !selectedGatewayData) {
      getGatewayDetails(editData.key);
    }
  }, [editData, gatewayOptions, selectedGatewayData]);

  /**
   * handleBackStep3
   */
  const handleNextStep1 = () => {
    if (selectedGateway) setStep(2);
  };
  /**
   * handleBackStep3
   */
  const handleBackStep2 = () => setStep(1);
  /**
   * handleBackStep3
   */
  const handleNextStep2 = () => setStep(3);
  /**
   * handleBackStep3
   */
  const handleBackStep3 = () => setStep(2);

  /**
   * handleSave
   */
  const handleSave = async () => {
    try {
      const credentials: Record<string, string> = {};

      selectedGatewayData?.configFields?.forEach((field: any) => {
        if (formData[field.key]) {
          credentials[field.key] = String(formData[field.key]);
        }
      });

      const payload = {
        gatewayKey: selectedGatewayData?.key,
        currency: selectedGatewayData?.supportedCurrencies?.[0],
        country: selectedGatewayData?.supportedCountries?.[0],
        useCases: isCPO
          ? (formData.businessType as string[])
          : [String(formData.businessType).toLowerCase()],
        paymentMethods: formData.paymentMethods,
        credentials,
      };

      console.log('payload', payload);

      if (editData) {
        await paymentGatewayApi.updatePaymentGateway(payload);
      } else {
        await paymentGatewayApi.createPaymentGateway(payload);
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.log(error);
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message || error?.message || 'Failed to save configuration',
          type: 'error',
        }),
      );
    }
  };

  /**
   * Validate if all required fields in step 2 are entered
   * @returns {boolean} True if valid
   */
  const isStep2Valid = (): boolean => {
    if (!selectedGatewayData?.configFields) {
      return true;
    }

    return selectedGatewayData.configFields.every((field: any) => {
      if (!field.required) {
        return true;
      }

      if (field.requiredFor && field.requiredFor.length > 0) {
        const hasSelectedPaymentMethod = field.requiredFor.some((method: string) =>
          (formData.paymentMethods as string[])?.includes(method),
        );
        if (!hasSelectedPaymentMethod) {
          return true;
        }
      }

      const value = formData[field.key];
      return value !== undefined && value !== null && String(value).trim() !== '';
    });
  };

  /**
   * buildReviewData constructs the data structure needed for the review step based on the selected gateway and form data
   * @returns {any} ReviewData object containing gateway name, logo, status, and configuration details for display in the review step
   */
  const buildReviewData = (): ReviewData => {
    const details: any[] = [];
    selectedGatewayData?.configFields?.forEach((field: any) => {
      if (field.requiredFor && field.requiredFor.length > 0) {
        const isApplicable = field.requiredFor.some((method: string) =>
          (formData.paymentMethods as string[])?.includes(method),
        );
        if (!isApplicable) {
          return;
        }
      }

      if (field.group) {
        if ((formData.paymentMethods as string[])?.includes(field.group)) {
          field.fields.forEach((subField: any) => {
            details.push({
              label: subField.label,
              value: subField.secret ? '*************' : String(formData[subField.key] || ''),
            });
          });
        }
      } else {
        details.push({
          label: field.label,
          value: field.secret ? '*************' : String(formData[field.key] || ''),
        });
      }
    });

    const logoMap: Record<string, string> = {
      stripe: 'S',
      hyperpay: 'hp',
    };

    return {
      gatewayName: selectedGatewayData?.displayName || '',
      gatewayLogo: logoMap[selectedGateway] || 'G',
      status: formData.status === 'active' ? 'Active' : 'Inactive',
      details,
    };
  };

  const reviewData = buildReviewData();

  return (
    <div className="gateway-main-container">
      {/* COMMON HEADER */}
      <div className="header">
        <h2 className="title">{editData ? 'Edit Gateway' : 'Configure Gateway'}</h2>
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="add-gateway-container">
          <div className="stepper-container">
            <div className="step-active">
              <span className="step-number">1</span>Select Gateway
            </div>
            <div className="stepper-line" />
            <div className="step-inactive">
              <span className="step-number">2</span>Configure
            </div>
            <div className="stepper-line" />
            <div className="step-inactive">
              <span className="step-number">3</span>Review
            </div>
          </div>
          <div className="form-content">
            <div className="form-content-header">
              <h3 className="section-title">Gateway Details</h3>
              <p className="section-subtitle">Select a gateway to configure</p>
            </div>
            <div className="form-container">
              <div className="business-type-radio-wrapper">
                <RadioButton
                  inputLabel="Business Type"
                  name="businessType"
                  value={getRadioButtonValue(formData.businessType)}
                  list={
                    isCPO
                      ? [
                        { label: 'B2C Payment', value: 'b2c' },
                        { label: 'B2B Payout', value: 'b2b' },
                        { label: 'Both', value: 'both' },
                      ]
                      : [
                        { label: 'B2C Payment', value: 'b2c' },
                        { label: 'B2B Payout', value: 'b2b' },
                      ]
                  }
                  onChangeValue={handleInputChange}
                />
              </div>
              <div className="selectFields-row">
                <SelectField
                  inputLabel="Country"
                  name="country"
                  options={[
                    { label: 'Saudi Arabia', value: 'SA' },
                    { label: 'UAE', value: 'AE' },
                    { label: 'United States', value: 'US' },
                    { label: 'India', value: 'IN' },
                  ]}
                  placeholder="Select Country"
                  selectedValue={formData.country as string}
                  onChangeValue={handleInputChange}
                />
              </div>
              <div className="selectFields-row">
                <SelectField
                  inputLabel="Select Payment Gateway"
                  name="gateway"
                  options={gatewayOptions.map((gateway: any) => ({
                    value: gateway.key,
                    label: gateway.displayName,
                  }))}
                  placeholder="Select Gateway"
                  selectedValue={selectedGateway}
                  onChangeValue={handleSelectChange}
                />
              </div>
              {selectedGatewayData && (
                <div className="payment-methods-selection-wrap">
                  <p className="input-label">Supported Payments Methods</p>

                  <div className="payment-methods-list">
                    {selectedGatewayData.supportedPaymentMethods.map((m: string) => {
                      const isSelected = (formData.paymentMethods as string[])?.includes(m);

                      const paymentMethodConfig: Record<
                        string,
                        {
                          label?: string;
                          logo?: React.ReactNode;
                        }
                      > = {
                        credit_card: {
                          label: 'Card',
                          logo: <CreditCardIcon className="method-logo-svg card-outline-icon" />,
                        },
                        debit_card: {
                          label: 'Debit Card',
                          logo: <VisaLogo className="method-logo-svg" />,
                        },
                        mada: {
                          label: 'Mada',
                          logo: <MadaLogoComponent />,
                        },
                        apple_pay: {
                          label: 'Apple Pay',
                          logo: <ApplePayLogo className="method-logo-svg" />,
                        },
                        google_pay: {
                          label: 'Google Pay',
                        },
                        stc_pay: {
                          label: 'STC Pay',
                        },
                      };

                      const config = paymentMethodConfig[m];

                      const label =
                        config?.label ||
                        m
                          .split('_')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ');

                      const firstLetter = label.charAt(0).toUpperCase();

                      /**
                       * Toggle payment method
                       */
                      const handleToggle = () => {
                        const currentMethods = (formData.paymentMethods as string[]) || [];

                        const newMethods = currentMethods.includes(m)
                          ? currentMethods.filter((item) => item !== m)
                          : [...currentMethods, m];

                        handleInputChange('paymentMethods', newMethods);
                      };

                      return (
                        <div
                          key={m}
                          className={`payment-method-select-box ${isSelected ? 'selected' : ''}`}
                          onClick={handleToggle}
                          role="presentation">
                          <div className="left-side">
                            {config?.logo ? (
                              config.logo
                            ) : (
                              <div className="method-logo-svg round-letter-logo">
                                {firstLetter}
                              </div>
                            )}

                            <span className="method-label">{label}</span>
                          </div>

                          {isSelected && (
                            <div className="check-indicator">
                              <span className="checkmark">✔</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="footer-actions">
            <MyButton label="Cancel" buttonType="outline" onClickFunc={onCancel} />
            <MyButton
              label="Next"
              buttonType="submit"
              disableButton={!selectedGateway}
              onClickFunc={handleNextStep1}
            />
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="configure-gateway-container">
          <div className="stepper-container">
            <div className="step-completed">
              <span className="step-icon">✔</span>Select Gateway
            </div>
            <div className="stepper-line-active" />
            <div className="step-active">
              <span className="step-number">2</span>Configure
            </div>
            <div className="stepper-line" />
            <div className="step-inactive">
              <span className="step-number">3</span>Review
            </div>
          </div>
          <div className="form-content-header">
            <h3 className="section-title">Configuration Details</h3>
            <p className="section-subtitle">Enter the required details for gateway configuration</p>
          </div>
          <div className="form-content">
            <div className="form-container">
              {/* COMMON FIELDS */}
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {selectedGatewayData?.configFields
                  ?.filter((field: any) => field.requiredFor.length === 0)
                  .map((field: any) => (
                    <div
                      className="input-group"
                      key={field.key}
                      style={{
                        width: '100%',
                        marginBottom: '16px',
                      }}>
                      <CustomInput
                        inputLabel={field.label}
                        name={field.key}
                        type={field.type as 'text' | 'password' | 'email' | 'number'}
                        placeholder={field.placeholder}
                        value={formData[field.key] as string}
                        onChangeValue={handleInputChange}
                        valueRequired={field.required}
                      />
                    </div>
                  ))}
              </div>
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {/* DYNAMIC PAYMENT METHOD GROUPS */}
                {(formData.paymentMethods as string[])?.map((method: string) => {
                  const fields = selectedGatewayData?.configFields?.filter((field: any) =>
                    field.requiredFor.includes(method),
                  );

                  if (!fields?.length) {
                    return null;
                  }

                  const methodLabels: Record<string, string> = {
                    credit_card: 'Card',
                    apple_pay: 'Apple Pay',
                    google_pay: 'Google Pay',
                  };

                  const displayGroupName =
                    methodLabels[method] ||
                    method
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                  return (
                    <div className="input-group-box" key={method}>
                      <h4 className="group-title">{displayGroupName}</h4>

                      {fields.map((field: any) => (
                        <div
                          className="input-group"
                          key={field.key}
                          style={{ marginBottom: '16px' }}>
                          <CustomInput
                            inputLabel={field.label}
                            name={field.key}
                            type={field.type as 'text' | 'password' | 'email' | 'number'}
                            placeholder={field.placeholder}
                            value={formData[field.key] as string}
                            onChangeValue={handleInputChange}
                            valueRequired={field.required}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="footer-actions">
            <MyButton label="Back" buttonType="outline" onClickFunc={handleBackStep2} />
            <MyButton
              label="Next"
              buttonType="submit"
              disableButton={!isStep2Valid()}
              onClickFunc={handleNextStep2}
            />
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div className="review-gateway-container">
          <div className="stepper-container">
            <div className="step-completed">
              <div className="step-icon">✓</div>
              <span>Select Gateway</span>
            </div>
            <div className="stepper-line-active" />
            <div className="step-completed">
              <div className="step-icon">✓</div>
              <span>Configure</span>
            </div>
            <div className="stepper-line-active" />
            <div className="step-active">
              <div className="step-number">3</div>
              <span>Review</span>
            </div>
          </div>
          <div className="header-container">
            <h2 className="title">Review Configuration</h2>
            <p className="subtitle">Please review the details before saving.</p>
          </div>
          <div className="form-content">
            <div className="form-container">
              <div
                className="details-card"
                style={{
                  marginBottom: '16px',
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                }}>
                <div
                  className="details-header"
                  style={{ marginBottom: '16px', fontWeight: 600, color: '#00a389' }}>
                  {reviewData.gatewayName} Gateway Details
                </div>
                <div className="details-list">
                  <div className="detail-row" style={{ display: 'flex', marginBottom: '8px' }}>
                    <div className="detail-label" style={{ width: '200px', color: '#666' }}>
                      Business Type
                    </div>
                    <div className="detail-value">
                      {getBusinessTypeLabel(formData.businessType)}
                    </div>
                  </div>
                  <div className="detail-row" style={{ display: 'flex', marginBottom: '8px' }}>
                    <div className="detail-label" style={{ width: '200px', color: '#666' }}>
                      Country
                    </div>
                    <div className="detail-value">{getCountryName(formData.country as string)}</div>
                  </div>
                  <div className="detail-row" style={{ display: 'flex', marginBottom: '8px' }}>
                    <div className="detail-label" style={{ width: '200px', color: '#666' }}>
                      Payment Method
                    </div>
                    <div className="detail-value">
                      {(formData.paymentMethods as string[])
                        .map((m) => {
                          const paymentMethodLabels: Record<string, string> = {
                            credit_card: 'Card',
                            debit_card: 'Debit Card',
                            mada: 'Mada',
                            apple_pay: 'Apple Pay',
                            google_pay: 'Google Pay',
                            stc_pay: 'STC Pay',
                          };

                          return (
                            paymentMethodLabels[m] ||
                            m
                              .split('_')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                          );
                        })
                        .join(', ')}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="details-card"
                style={{
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                }}>
                <div
                  className="details-header"
                  style={{ marginBottom: '16px', fontWeight: 600, color: '#00a389' }}>
                  Configuration Details
                </div>
                <div className="details-list">
                  {reviewData.details.map((detail, index) => (
                    <div
                      className="detail-row"
                      key={index}
                      style={{ display: 'flex', marginBottom: '8px' }}>
                      <div
                        className="detail-label"
                        style={{ width: '200px', color: '#666', wordBreak: 'break-all' }}>
                        {detail.label}
                      </div>
                      <div className="detail-value" style={{ wordBreak: 'break-all' }}>
                        {detail.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="configuration-btn">
                <MyButton
                  label="Test Configuration"
                  buttonType="primary-outline"
                  onClickFunc={() => { }}
                />
              </div>
            </div>
          </div>
          <div className="footer-actions">
            <div className="left-actions">
              <MyButton label="Back" buttonType="outline" onClickFunc={handleBackStep3} />
            </div>
            <div className="right-actions">
              <MyButton label="Cancel" buttonType="outline" onClickFunc={onCancel} />
              <MyButton label="Save Configuration" buttonType="submit" onClickFunc={handleSave} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

AddGateway.defaultProps = {
  /**
   * onCancel default function
   */
  onCancel: () => { },
  /**
   * onCancel default function
   */
  onSuccess: () => { },
  editData: null,
};
export default AddGateway;
